import cn from "classnames";
import { type ComponentProps, type FC, useId, useState } from "react";
import { useClickOutside } from "../hooks/use-click-outside";
import { Button } from "./Button";
import "./ButtonDropdownMenu.css";

type ButtonProps = Omit<
	ComponentProps<typeof Button>,
	"children" | "variant" | "full"
>;

export type DropdownActionProps = {
	label: string;
	props: ButtonProps;
};

type Props = {
	main: DropdownActionProps;
	options: Array<DropdownActionProps>;
};

export const ButtonDropdownMenu: FC<Readonly<Props>> = ({ main, options }) => {
	const id = useId();
	const [active, setActive] = useState(false);
	const ref = useClickOutside<HTMLDivElement>(() => setActive(false));

	const hasMainAction = !!main.props.onClick;

	return (
		<div ref={ref} className={cn("btn-dropdown-menu", { active })}>
			<header>
				<Button
					{...main.props}
					onClick={
						hasMainAction ? main.props.onClick : () => setActive(!active)
					}
				>
					{main.label}
				</Button>
				<Button
					aria-atomic
					aria-label="dropdown menu trigger"
					aria-description={
						active ? "dropdown menu open" : "dropdown menu closed"
					}
					icon={"keyboard_arrow_down"}
					className={cn("trigger", active)}
					onClick={() => setActive(!active)}
				/>
			</header>

			{active && (
				<ul>
					{options.map(({ label, props }) => (
						<li key={`${label}-${id}`}>
							<Button
								variant="ghost"
								icon={props.icon}
								onClick={props.onClick}
								disabled={props.disabled}
								title={props.title}
							>
								{label}
							</Button>
						</li>
					))}
				</ul>
			)}
		</div>
	);
};
